// ---------------------------------------------------------------------------
// Every route in the application.
//
// The routes for screens that are not built are generated from the same
// NAV_ITEMS list that draws the sidebar, so a menu entry and its route can
// never drift apart. Each one is wrapped in RequirePermission, meaning typing
// the address directly is refused exactly as clicking a hidden link would be.
//
// ---------------------------------------------------------------------------
// WHAT IS IN THE FIRST DOWNLOAD, AND WHY
//
// By Phase 7 the single bundle had reached 614 kB, and almost none of it was
// the till. A cashier opening the POS was waiting for the purchasing screens,
// the recipe editor and the audit log to arrive first.
//
// So exactly three things load eagerly:
//
//   * the login page      - it is the first screen anybody sees
//   * the POS             - the screen the shop lives in all day
//   * the shell           - layout, guards, redirects, error pages
//
// Everything else is `lazy()`. They arrive on first visit, behind a spinner
// that leaves the sidebar and header in place, and are cached from then on.
// That ordering is deliberate: a manager opening Reports once a day can wait a
// moment; a cashier with a queue cannot.
// ---------------------------------------------------------------------------

import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { RedirectIfAuthenticated, RequireAuth, RequirePermission } from './ProtectedRoute';
import { NAV_ITEMS } from './navigation';
import { PageLoading } from '../components/common/StateBlocks';

// --- eager: the till and the way in ----------------------------------------
import { LoginPage } from '../pages/LoginPage';
import { PosPage } from '../pages/PosPage';
import { UnauthorizedPage } from '../pages/UnauthorizedPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { NotBuiltYetPage } from '../pages/NotBuiltYetPage';
import { LandingRedirect } from './LandingRedirect';
// Phase 9: needed precisely when a lazy chunk could not be downloaded.
import { OfflineQueuePage } from '../pages/OfflineQueuePage';

// --- lazy: everything a person opens occasionally --------------------------
// Each of these becomes its own chunk, fetched the first time somebody goes
// there. Named exports need the `.then` shim; that is the only reason for it.
const DashboardPage = lazy(() =>
  import('../pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const EmployeesPage = lazy(() =>
  import('../pages/EmployeesPage').then((m) => ({ default: m.EmployeesPage })),
);
const AuditLogPage = lazy(() =>
  import('../pages/AuditLogPage').then((m) => ({ default: m.AuditLogPage })),
);
const ProductsPage = lazy(() =>
  import('../pages/ProductsPage').then((m) => ({ default: m.ProductsPage })),
);
const QuickPricingPage = lazy(() =>
  import('../pages/QuickPricingPage').then((m) => ({ default: m.QuickPricingPage })),
);
const OffersPage = lazy(() =>
  import('../pages/OffersPage').then((m) => ({ default: m.OffersPage })),
);
const MenuManagementPage = lazy(() =>
  import('../pages/MenuManagementPage').then((m) => ({ default: m.MenuManagementPage })),
);
const ShiftPage = lazy(() => import('../pages/ShiftPage').then((m) => ({ default: m.ShiftPage })));
const OrdersPage = lazy(() =>
  import('../pages/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);
const MenuOrdersPage = lazy(() =>
  import('../pages/MenuOrdersPage').then((m) => ({ default: m.MenuOrdersPage })),
);
const ReportsPage = lazy(() =>
  import('../pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const InventoryPage = lazy(() =>
  import('../pages/InventoryPage').then((m) => ({ default: m.InventoryPage })),
);
const ExpensesPage = lazy(() =>
  import('../pages/ExpensesPage').then((m) => ({ default: m.ExpensesPage })),
);
const SuppliersPage = lazy(() =>
  import('../pages/SuppliersPage').then((m) => ({ default: m.SuppliersPage })),
);
const PurchasesPage = lazy(() =>
  import('../pages/PurchasesPage').then((m) => ({ default: m.PurchasesPage })),
);
// PAUSED. The real screens are still on disk and still compile; the routes
// point at their Coming Soon pages instead, which is the whole of the change
// and the whole of what has to be undone to bring them back. Nothing in the
// database, the Prisma models, the migrations or the permissions is touched.
const RecipesPage = lazy(() =>
  import('../pages/RecipesComingSoon').then((m) => ({ default: m.RecipesComingSoon })),
);
const ProductionPage = lazy(() =>
  import('../pages/ProductionComingSoon').then((m) => ({ default: m.ProductionComingSoon })),
);
const WastePage = lazy(() => import('../pages/WastePage').then((m) => ({ default: m.WastePage })));
// PAUSED like Recipes and Production above: the real screen is still on disk
// at CustomersPage.tsx, the data and every relation stay served, and the
// till's customer picker keeps working. Only this management surface waits.
const CustomersPage = lazy(() =>
  import('../pages/CustomersComingSoon').then((m) => ({ default: m.CustomersComingSoon })),
);
const CustomerCreditPage = lazy(() =>
  import('../pages/CustomerCreditPage').then((m) => ({ default: m.CustomerCreditPage })),
);
// Phase 8. The import wizard pulls in nothing heavy of its own - the
// spreadsheet parsing is all on the server - but it is still a screen an owner
// opens a handful of times a year, so it has no business in the till's first
// download.
const ImportPage = lazy(() =>
  import('../pages/ImportPage').then((m) => ({ default: m.ImportPage })),
);
const BackupsPage = lazy(() =>
  import('../pages/BackupsPage').then((m) => ({ default: m.BackupsPage })),
);
// Phase 9. The queue screen is NOT lazy: it is the screen a cashier needs
// when the connection has just gone, which is exactly the moment a lazy
// chunk cannot be fetched. It is small, and it lives in the shell.
const LabelsPage = lazy(() =>
  import('../pages/LabelsComingSoon').then((m) => ({ default: m.LabelsComingSoon })),
);
const SettingsPage = lazy(() =>
  import('../pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

/// Screens that really exist, mapped by path.
const BUILT_PAGES: Record<string, ComponentType> = {
  '/dashboard': DashboardPage,
  '/employees': EmployeesPage,
  '/audit-log': AuditLogPage,
  // --- Phase 2 ---
  '/products': ProductsPage,
  '/pricing/quick': QuickPricingPage,
  '/pricing/offers': OffersPage,
  '/menu': MenuManagementPage,
  // The POS is NOT lazy. It is the screen the shop lives in.
  '/pos': PosPage,
  // --- Phase 3 ---
  '/shifts': ShiftPage,
  '/orders': OrdersPage,
  '/menu-orders': MenuOrdersPage,
  '/reports': ReportsPage,
  // --- Phase 4 ---
  '/inventory': InventoryPage,
  '/expenses': ExpensesPage,
  // --- Phase 5 ---
  '/suppliers': SuppliersPage,
  // Batches and expiry live as a tab on /inventory, not a route of their own.
  '/purchases': PurchasesPage,
  // --- Phase 6 ---
  '/recipes': RecipesPage,
  '/production': ProductionPage,
  '/waste': WastePage,
  // Production and waste reports live as tabs on /reports.
  // --- Phase 7 ---
  '/customers': CustomersPage,
  '/customer-credit': CustomerCreditPage,
  // --- Phase 8 ---
  // The report suite lives on /reports; notifications live in the header.
  '/import': ImportPage,
  '/backups': BackupsPage,
  // --- Phase 9 ---
  '/offline-queue': OfflineQueuePage,
  '/labels': LabelsPage,
  // Nine sections of shop configuration, opened a few times a year - exactly
  // the sort of screen that has no business being in the till's first
  // download.
  '/settings': SettingsPage,
};

export function AppRoutes() {
  return (
    <Routes>
      {/* --- public ---------------------------------------------------- */}
      <Route
        path="/login"
        element={
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        }
      />

      {/* Reachable without a session, so a signed-out person following an old
          link gets an explanation rather than a redirect loop. */}
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* --- signed in -------------------------------------------------- */}
      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route path="/customer-orders" element={
            <RequirePermission permission="pos.access">
              <RequirePermission permission="orders.delivery_view">
                <Navigate to="/pos?view=accounts" replace />
              </RequirePermission>
            </RequirePermission>
          } />
          {/* Stock counts were merged into «المخزون»: an old bookmark lands there. */}
          <Route path="/stock-counts" element={<Navigate to="/inventory" replace />} />
          {NAV_ITEMS.map((item) => {
            const BuiltPage = BUILT_PAGES[item.path];

            return (
              <Route
                key={item.path}
                path={item.path}
                element={
                  <RequirePermission permission={item.permission}>
                    {/* The boundary sits INSIDE the layout, so the sidebar and
                        header stay put while a lazy screen arrives. A full-page
                        flash on every navigation would be worse than the wait
                        it is covering. */}
                    <Suspense fallback={<PageLoading />}>
                      {BuiltPage ? <BuiltPage /> : <NotBuiltYetPage item={item} />}
                    </Suspense>
                  </RequirePermission>
                }
              />
            );
          })}
        </Route>
      </Route>

      {/* Send "/" to whichever screen this person may actually open. */}
      <Route path="/" element={<LandingRedirect />} />

      <Route path="/not-found" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/not-found" replace />} />
    </Routes>
  );
}
